// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { Loader2, RefreshCw, Users, KeyRound, UserPlus, Link2 } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { DataGrid, type DataGridColumn } from '../../components/DataGrid'

/**
 * D-CRX-49 — the global "All nurses" admin surface. Nurses lived in two disconnected worlds: staffing
 * ROSTER records (per-agency, mostly login-less) and provider_nurse LOGIN accounts (who do CareRx visits
 * but were never rostered). This view shows every rostered nurse with whether they hold a login, and a
 * second panel of login-holding nurses who are NOT on the roster — with one click to add them.
 */
export default function CareRxAllNurses() {
  const [nurses, setNurses] = useState<api.NurseRow[] | null>(null)
  const [orphans, setOrphans] = useState<api.UnrosteredNurse[]>([])
  const [msg, setMsg] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    api.listCareRxNurses().then(r => setNurses(r.items)).catch(e => setMsg(friendlyError(e)))
    api.listCareRxUnrosteredNurses().then(r => setOrphans(r.items)).catch(() => setOrphans([]))
  }, [])
  useEffect(() => { load() }, [load])

  const addToRoster = async (o: api.UnrosteredNurse) => {
    setBusy(true); setMsg('')
    try {
      await api.createCareRxNurse({ display_name: o.name || o.email || 'Nurse', account_id: o.account_id })
      setMsg(`✅ ${o.name || o.email} added to the roster and linked to their login.`)
      load()
    } catch (e) { setMsg(friendlyError(e)) } finally { setBusy(false) }
  }

  const linked = (nurses ?? []).filter(n => n.login).length
  const loginBadge = (n: api.NurseRow) => n.login ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: '#0a7a1d', background: '#e9f7ec', borderRadius: 8, padding: '1px 8px' }}>
      <KeyRound size={11} /> {n.login.email ?? n.login.name}{n.login.status && n.login.status !== 'active' ? ` · ${n.login.status}` : ''}
    </span>
  ) : (
    <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e', background: '#fef3c7', borderRadius: 8, padding: '1px 8px' }}>no login · agency</span>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={title}><Users size={16} /> All nurses</div>
        <span style={{ ...muted, fontSize: 12 }}>every nurse across agencies · login linkage</span>
        <button onClick={load} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer' }}><RefreshCw size={14} /></button>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        <div style={kpi}><div style={kpiNum}>{nurses?.length ?? '—'}</div><div style={kpiLbl}>ON ROSTER</div></div>
        <div style={kpi}><div style={kpiNum}>{nurses ? linked : '—'}</div><div style={kpiLbl}>WITH A LOGIN</div></div>
        <div style={kpi}><div style={kpiNum}>{orphans.length}</div><div style={kpiLbl}>LOGINS NOT ROSTERED</div></div>
      </div>

      {msg && <div style={{ fontSize: 12.5, color: msg.startsWith('✅') ? '#15803d' : '#b91c1c' }}>{msg}</div>}

      {/* Roster */}
      <div>
        <div style={sectionH}>NURSE ROSTER</div>
        {nurses === null ? <div style={{ ...muted, fontSize: 12.5 }}><Loader2 size={13} className="spin" /> Loading…</div>
          : nurses.length === 0 ? <div style={{ ...muted, fontSize: 12.5 }}>No nurses on the roster yet.</div> : (
          <DataGrid
            rows={nurses}
            columns={rosterColumns(loginBadge)}
            storageKey="carerx-all-nurses"
            getRowKey={n => n.id}
            emptyText="No nurses on the roster yet."
          />
        )}
      </div>

      {/* Orphan logins */}
      <div>
        <div style={sectionH}>LOGINS NOT ON THE ROSTER <span style={{ ...muted, fontWeight: 400, textTransform: 'none' }}>— nurses with a login &amp; CareRx visits, not yet rostered</span></div>
        {orphans.length === 0 ? <div style={{ ...muted, fontSize: 12.5 }}>None — every login-holding nurse is on the roster.</div> : (
          orphans.map(o => (
            <div key={o.account_id} style={{ ...card, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <KeyRound size={14} style={{ color: 'var(--color-primary)' }} />
              <b>{o.name || '—'}</b>
              <span style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>{o.email}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#6d28d9', background: '#ede9fe', borderRadius: 8, padding: '1px 8px' }}>{o.visit_count} visit{o.visit_count === 1 ? '' : 's'}</span>
              {o.status && o.status !== 'active' && <span style={{ fontSize: 11, color: '#92400e' }}>{o.status}</span>}
              <button disabled={busy} onClick={() => addToRoster(o)} style={{ ...primaryBtn, marginLeft: 'auto', display: 'inline-flex', gap: 5, alignItems: 'center' }}>
                <UserPlus size={13} /> Add to roster
              </button>
            </div>
          ))
        )}
        <div style={{ ...muted, fontSize: 11.5, marginTop: 6, display: 'flex', gap: 5, alignItems: 'center' }}>
          <Link2 size={12} /> Adding a login to the roster links it (roster nurse_id = the account) so staffing, teams, and the nurse's own dashboard all point to one identity.
        </div>
      </div>
    </div>
  )
}

function rosterColumns(loginBadge: (n: api.NurseRow) => ReactNode): DataGridColumn<api.NurseRow>[] {
  return [
    { key: 'display_name', header: 'Nurse', width: 180, value: n => n.display_name, render: n => <b>{n.display_name}</b> },
    { key: 'login', header: 'Login', width: 200, value: n => n.login?.email ?? n.login?.name ?? '', render: n => loginBadge(n) },
    { key: 'competencies', header: 'Competencies', width: 180, value: n => n.competencies || '—' },
    { key: 'territory_zips', header: 'Territory', width: 220, value: n => n.territory_zips || '—' },
    { key: 'team_count', header: 'Teams', width: 80, align: 'right', value: n => n.team_count },
    { key: 'active', header: 'Active', width: 80, align: 'center', value: n => (n.active ? 1 : 0), render: n => (n.active ? '✓' : '—') },
  ]
}

const muted: CSSProperties = { color: 'var(--color-text-soft, #6b7280)' }
const title: CSSProperties = { fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }
const sectionH: CSSProperties = { fontSize: 12, fontWeight: 800, letterSpacing: .3, color: 'var(--color-text-soft)', marginBottom: 8 }
const card: CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 8, padding: 10, marginBottom: 8 }
const primaryBtn: CSSProperties = { fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none', background: 'var(--color-primary, #0d9624)', color: '#fff', borderRadius: 6, padding: '5px 12px' }
const kpi: CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 10, padding: '10px 14px', background: 'var(--color-surface)' }
const kpiNum: CSSProperties = { fontSize: 24, fontWeight: 800, lineHeight: 1, color: 'var(--color-text)' }
const kpiLbl: CSSProperties = { fontSize: 10, fontWeight: 700, letterSpacing: .5, color: 'var(--color-text-soft)', marginTop: 6 }
